import { useLayoutEffect, useRef, useState } from 'react'

/**
 * Detects whether a single-line (CSS-ellipsis) element is actually overflowing, so callers can show a
 * tooltip only when the text is clipped. This is the single-line counterpart to react-text-truncate's
 * `onTruncated` callback, which only fires for its own multi-line clamping.
 *
 * Attach `ref` to the element that carries `noWrap`/`text-overflow: ellipsis`. `text` is used only as a
 * re-measure trigger when the content changes. Pass `enabled: false` to skip measuring entirely (and
 * avoid the per-element ResizeObserver) when the caller doesn't need the result.
 */
const useIsTextTruncated = <T extends HTMLElement = HTMLElement>(text?: string, enabled = true) => {
  const ref = useRef<T>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element || !enabled) return

    const measure = () => setIsTruncated(element.scrollWidth > element.clientWidth)
    measure()

    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [text, enabled])

  return { ref, isTruncated }
}

export default useIsTextTruncated
